import axios from "axios";
import constant from "../constant";
import { enqueueRequest, flushQueue } from "../utils/offlineQueue";
import { getAuthHeader, clearAuthSession, parseApiErrorMessage } from "../utils";
import { MSG } from "../constants/userMessages";

let flushing = false;

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const AUTH_FAILURE_RE =
  /unauthorized|token missing|session expired|please login again/i;

function redirectToLogin() {
  clearAuthSession();
  const loginUrl = constant.nonAuthedcomponent?.login?.url || "/login";
  if (!window.location.pathname.startsWith(loginUrl)) {
    window.location.assign(loginUrl);
  }
}

function isNetworkFailure(error) {
  if (!error) return false;
  if (error.code === "ERR_NETWORK") return true;
  if (!error.response && !navigator.onLine) return true;
  return false;
}

async function flushOfflineQueue() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    await flushQueue(async (item) => {
      const auth = getAuthHeader();
      const headers = { ...auth.headers, ...(item.headers || {}) };
      if (item.body instanceof FormData) {
        delete headers["Content-Type"];
      }
      await axios({
        method: item.method,
        url: item.url,
        data: item.body,
        headers,
        withCredentials: true,
      });
    });
    window.dispatchEvent(new CustomEvent("elog-sync-flush"));
  } finally {
    flushing = false;
  }
}

export function setupAxiosInterceptors() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const authMessage = parseApiErrorMessage(error?.response?.data, "");

      if (status === 401 || (status === 403 && AUTH_FAILURE_RE.test(authMessage))) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (status === 429) {
        const config = error?.config;
        return Promise.resolve({
          data: {
            status: false,
            allowed: false,
            error: {
              message: parseApiErrorMessage(
                error?.response?.data,
                "Too many requests. Please wait a moment and try again."
              ),
            },
          },
          status: 429,
          statusText: error?.response?.statusText || "Too Many Requests",
          headers: error?.response?.headers || {},
          config,
        });
      }

      const config = error?.config;
      const method = String(config?.method || "").toUpperCase();

      // Read calls: return API error body instead of uncaught runtime overlay (403 vessel scope, etc.)
      if (
        config &&
        !MUTATING.has(method) &&
        isNetworkFailure(error)
      ) {
        return Promise.resolve({
          data: {
            status: false,
            allowed: false,
            error: {
              message: MSG.network,
            },
          },
          status: 0,
          statusText: "Network Error",
          headers: {},
          config,
        });
      }

      if (
        config &&
        !MUTATING.has(method) &&
        status >= 400 &&
        status < 500 &&
        error.response?.data
      ) {
        return Promise.resolve({
          data: error.response.data,
          status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          config,
        });
      }

      if (
        config &&
        !config.__offlineQueued &&
        MUTATING.has(String(config.method || "").toUpperCase()) &&
        isNetworkFailure(error)
      ) {
        const url = config.url || "";
        await enqueueRequest({
          method: config.method,
          url,
          data: config.data,
          headers: config.headers,
        });
        config.__offlineQueued = true;
        return Promise.resolve({
          data: {
            status: true,
            allowed: true,
            result: { queued: true, offline: true },
          },
          status: 202,
          config,
        });
      }

      return Promise.reject(error);
    }
  );

  window.addEventListener("online", flushOfflineQueue);
  if (navigator.onLine) {
    setTimeout(() => flushOfflineQueue(), 3000);
  }
}
